<template>
  <div class="mt-4 px-8 flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <div class="text-2xl font-bold">DMGW6 Board</div>
      <div class="flex items-center gap-3">
        <DmgwQtoolConnectedIndicator
          :connection-state="connection.state.value"
        />
        <div class="flex items-center rounded-full">
          <button
            :class="[
              'rounded-full px-4 py-1.5 text-sm transition-colors focus:outline-none',
              activePage === 'verbindungen'
                ? 'bg-light-3 dark:bg-dark-3 font-semibold'
                : '',
            ]"
            @click="activePage = 'verbindungen'"
          >
            Verbindungen
          </button>
          <button
            :class="[
              'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors focus:outline-none',
              activePage === 'hilfe'
                ? 'bg-light-3 dark:bg-dark-3 font-semibold'
                : '',
            ]"
            @click="activePage = 'hilfe'"
          >
            <em class="mdi mdi-help-circle-outline" />
            DMGW Hilfe
          </button>
        </div>
        <button @click="router.back()">
          <em class="mdi mdi-close text-2xl" />
        </button>
      </div>
    </div>

    <template v-if="activePage === 'verbindungen'">
      <BaseInformation
        v-if="!bluetoothEnabled"
        :status="Status.WARNING"
        icon="mdi-bluetooth-off"
        information-text="Bluetooth ist ausgeschaltet. Bitte aktiviere Bluetooth, um mit dem Q-Tool fortzufahren."
      />

      <template v-else>
        <DmgwQtoolConnect
          :connection-state="connection.state.value"
          @scan="connection.scan()"
          @connect="connection.connect($event)"
        />

        <!--
          Debug only. The availability check fails safe to "not required" on
          every error path (missing driver, unreadable version, 30 s timeout),
          so a Q-Tool that is already current — or one whose version could not
          be read — never reaches the firmware screen. This forces the run so
          the update path can be exercised on demand. Native accepts
          StartFirmwareUpdate in Connected mode already, so no bridge change is
          needed; the SDK decides whether the image applies and answers with
          UPDATE_NOT_POSSIBLE when it does not.
        -->
        <button
          v-if="
            isDevelopmentEnvironment &&
            connection.isConnected.value &&
            !firmwareBlocking
          "
          class="self-start rounded-lg border border-dashed border-primary-2 px-3 py-1.5 text-sm text-primary-2"
          data-testid="dmgwBoard_forceFirmwareUpdate"
          @click="startFirmwareUpdate()"
        >
          <em class="mdi mdi-robot-outline mr-1.5" />
          Debug: Firmware-Update erzwingen
        </button>

        <DmgwGatewayDiscovery
          v-if="
            !firmwareBlocking &&
            (connection.isConnected.value ||
              operations.gatewayDiscovery.value.status !== 'idle') &&
            operations.gatewayReadout.value.status === 'idle'
          "
          :discovery-state="operations.gatewayDiscovery.value"
          :is-connected="!isQtoolLost"
          @start-scan="operations.startGatewayDiscovery()"
          @select-gateway="operations.startGatewayReadout($event)"
        />

        <DmgwGatewayReadout
          v-if="
            !firmwareBlocking &&
            operations.gatewayReadout.value.status !== 'idle'
          "
          :readout-state="operations.gatewayReadout.value"
          :device-readout-state="operations.deviceReadout.value"
          :is-connected="!isQtoolLost"
          @retry="retryGatewayReadout()"
          @cancel="handleCancelReadout()"
        />
      </template>
    </template>

    <DmgwHelp v-else />

    <QToolFirmwareUpdateDialog
      :active="firmwareBlocking"
      :state="firmwareState"
      :dismissing="firmwareDismissing"
      @start="startFirmwareUpdate"
      @dismiss="dismissFirmwareUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useQToolConnection } from '@/composables/useQToolConnection';
import { useQToolOperations } from '@/composables/useQToolOperations';
import { useQToolFirmwareUpdate } from '@/composables/useQToolFirmwareUpdate';
import { useQToolBluetoothState } from '@/composables/useQToolBluetoothState';
import DmgwQtoolConnect from './DmgwQtoolConnect.vue';
import DmgwGatewayDiscovery from './DmgwGatewayDiscovery.vue';
import DmgwGatewayReadout from './DmgwGatewayReadout.vue';
import DmgwQtoolConnectedIndicator from './DmgwQtoolConnectedIndicator.vue';
import DmgwHelp from '@/components/DmgwBoard/DmgwHelp.vue';
import QToolFirmwareUpdateDialog from '@/components/QTool/QToolFirmwareUpdateDialog.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import { Status } from '@/enums/generic';
import { default as envConfig } from '@/config/runtimeConfig';

const router = useRouter();
const activePage = ref<'verbindungen' | 'hilfe'>('verbindungen');
const connection = useQToolConnection();
const operations = useQToolOperations(connection);
const {
  state: firmwareState,
  isBlocking: firmwareBlocking,
  dismissing: firmwareDismissing,
  startUpdate: startFirmwareUpdate,
  dismiss: dismissFirmwareUpdate,
} = useQToolFirmwareUpdate();
const { enabled: bluetoothEnabled } = useQToolBluetoothState();

const isQtoolLost = computed(() => {
  const phase = connection.state.value.phase;
  return phase === 'disconnected' || phase === 'error';
});

// Same gate the side-nav dev icon uses, so the button is absent from qa/prod
// bundles rather than merely hidden behind a flag nobody flips.
const isDevelopmentEnvironment = computed(() => {
  return envConfig.isDevelopment === 'true';
});

watch(
  bluetoothEnabled,
  (enabled) => {
    if (enabled && connection.state.value.phase === 'idle') {
      connection.scan();
    }
  },
  { immediate: true }
);

watch(
  [connection.isConnected, firmwareBlocking, bluetoothEnabled],
  ([connected, fwBlocking, btEnabled]) => {
    if (
      connected &&
      !fwBlocking &&
      btEnabled &&
      operations.gatewayDiscovery.value.status === 'idle'
    ) {
      operations.startGatewayDiscovery();
    }
  },
  { immediate: true }
);

function retryGatewayReadout() {
  if (isQtoolLost.value) {
    operations.cancelGatewayReadout();
    connection.scan();
    return;
  }
  const readout = operations.gatewayReadout.value;
  if ('gateway' in readout) {
    operations.startGatewayReadout(readout.gateway);
  }
}

function handleCancelReadout() {
  if (connection.isConnected.value) {
    operations.cancelGatewayReadout({ notifyNative: false });
    operations.startGatewayDiscovery();
  } else {
    operations.cancelGatewayReadout();
  }
}
</script>
